import { type Change, Mark, TextRange, type Value } from '@gitbook/slate';
import type { Plugin } from '@gitbook/slate-react';
import { Set as ISet } from '@gitbook/util-immutable';
import { assert } from '@gitbook/util-ts';

import { isEscape } from './common';

/**
 * Generic Slate plugin to detect user typing specific symbols.
 */
export function createTypePlugin<TData>(args: {
    symbols: string[];
    getData: (input: { value: Value; symbol: string; textValue: string }) => TData | null;
    markType: string;
    renderMark: NonNullable<Plugin['renderMark']>;
}): Plugin {
    const captureRegExp = new RegExp(`(^|\\s)([${args.symbols.join('')}])(\\w*)$`);

    function matchValue(value: Value) {
        if (!value.isCollapsed) {
            return null;
        }

        const { startText, startBlock, selection } = value;

        if (!TextRange.isTextRange(selection)) {
            return null;
        }

        // In some cases, like if the node that was selected gets deleted,
        // `startText` can be null.
        if (!startText || !startBlock) {
            return null;
        }

        const { startOffset } = selection;
        const textBefore = startText.text.slice(0, startOffset);
        const result = captureRegExp.exec(textBefore);

        if (!result) {
            return null;
        }

        const symbol = result[2];
        const textValue = result[3];
        assert(symbol !== undefined);
        assert(textValue !== undefined);

        return { symbol, textValue };
    }
    return {
        onBeforeInput: (event, change) => {
            if (!(event instanceof InputEvent)) {
                return;
            }

            const { data } = event;
            if (event.inputType !== 'insertText' || !data) {
                return;
            }

            const { value } = change;
            const { selection } = value;

            if (!TextRange.isTextRange(selection)) {
                return;
            }

            const insertAndAddMark = () => {
                event.preventDefault();
                change.insertText(data);

                const match = matchValue(change.value);
                if (match) {
                    const data = args.getData({
                        ...match,
                        value: change.value,
                    });
                    if (!data) {
                        return;
                    }
                    addMarkToSelection(change, {
                        type: args.markType,
                        data,
                    });
                }

                return change;
            };

            // // If we have a mark, we keep it.
            const existingMark = selection.marks?.find((mark) => mark.type === args.markType);
            if (existingMark) {
                return insertAndAddMark();
            }

            const symbol = args.symbols.find((s) => s === event.data);
            if (!symbol) {
                return;
            }

            return insertAndAddMark();
        },
        onKeyDown: (event, change) => {
            // If the user presses Escape, we check if there's any active mark of this type.
            if (isEscape(event)) {
                const hasMark = change.value.decorations.find(
                    (range) => range.marks?.some((mark) => mark.type === args.markType) ?? false
                );
                if (hasMark) {
                    return change;
                }
            }
        },
        decorateValue: (value: Value) => {
            const { selection } = value;

            if (
                !TextRange.isTextRange(selection) ||
                !selection.marks?.some((mark) => mark.type === args.markType)
            ) {
                return;
            }

            const match = matchValue(value);

            if (!match) {
                return;
            }

            const data = args.getData({
                ...match,
                value,
            });

            if (!data) {
                return;
            }

            return [
                TextRange.create({
                    anchorKey: selection.startKey,
                    anchorOffset: selection.startOffset - match.textValue.length - 1,
                    focusKey: selection.startKey,
                    focusOffset: selection.startOffset,
                    marks: [{ type: args.markType, data }],
                    isAtomic: true,
                }),
            ];
        },
        renderMark: (props) => {
            const { mark } = props;
            if (mark.type === args.markType) {
                return args.renderMark(props);
            }
            return null;
        },
    };
}

/**
 * Add a mark to the current selection.
 */
function addMarkToSelection(change: Change, mark: { type: string; data: object }) {
    const { selection } = change.value;
    if (!TextRange.isTextRange(selection)) {
        return;
    }
    change.select(
        selection.set('marks', (selection.marks ?? ISet.create()).add(Mark.create(mark)))
    );
}
